import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

/**
 * Aba "Qualidade dos bots" das Métricas: o que o juiz (claude-opus-5-5)
 * achou das respostas dos bots numa amostra diária de conversas
 * (GET /admin/metrics/bot-quality). Período vem do filtro das Métricas.
 */

interface Agregado {
  conversas: number
  notaMedia: number | null
  respostas: number
  taxaAcerto: number | null
  taxaIncorretas: number | null
  conversasSemErro: number | null
  taxaInvencao: number | null
  taxaTransfDesnecessaria: number | null
  taxaFaltouTransferir: number | null
  taxaPediuOQueTinha: number | null
  taxaResolucao: number | null
  resolucaoIndeterminada: number
  corrigidos: number
  juizConcorda: number
  taxaConcordancia: number | null
  juizApontou: number
  apontadosConfirmados: number
}

interface Caso {
  chatId: number
  protocolo: string | null
  agente: string
  dia: string
  nota: number
  veredito: string
  resolveu: string
  transferencia: string
  invencao: boolean
  pediuOQueTinha: boolean
  justificativa: string | null
  trechoErrado: string | null
  respostaCerta: string | null
  correcaoHumana: string | null
}

interface Painel {
  semDados?: boolean
  motivo?: string
  total: Agregado
  porAgente: Array<Agregado & { agentId: number; agente: string }>
  porDia: Array<Agregado & { dia: string }>
  porDiaAgente: Array<{
    dia: string
    agentId: number
    agente: string
    notaMedia: number | null
    taxaAcerto: number | null
    conversas: number
  }>
  comparacao: null | {
    corte: string
    antes: Agregado
    depois: Agregado
    porAgente: Array<{
      agentId: number
      agente: string
      antes: Agregado
      depois: Agregado
    }>
  }
  piores: Caso[]
  discordancias: Caso[]
  correcoesHumanas: Array<{
    agentId: number
    agente: string
    correcoes: number
    correcoesTexto: number
    intervencoes: number
    chats: number
  }>
  custoUsd: number
  config: {
    enabled: boolean
    perAgent: number
    model: string
    effort: string
    maxUsdPerDay: number
  }
}

/** Deploy de referência pedido pelo Thiago (visão 06/10 ~20:06). */
const CORTE_SUGERIDO = '2026-10-06T20:06'

const fmtPct = (v: number | null | undefined) =>
  v == null ? '-' : `${v.toLocaleString('pt-BR')}%`
const fmtNota = (v: number | null | undefined) =>
  v == null ? '-' : v.toLocaleString('pt-BR')
const fmtDia = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`

const METRICAS: Array<{
  chave: keyof Agregado
  rotulo: string
  melhorMaior: boolean
  nota?: boolean
}> = [
  {
    chave: 'notaMedia',
    rotulo: 'Nota média (0-10)',
    melhorMaior: true,
    nota: true,
  },
  { chave: 'taxaAcerto', rotulo: 'Acerto das respostas', melhorMaior: true },
  {
    chave: 'conversasSemErro',
    rotulo: 'Conversas sem erro',
    melhorMaior: true,
  },
  {
    chave: 'taxaInvencao',
    rotulo: 'Conversas com invenção',
    melhorMaior: false,
  },
  {
    chave: 'taxaTransfDesnecessaria',
    rotulo: 'Transferência desnecessária',
    melhorMaior: false,
  },
  {
    chave: 'taxaFaltouTransferir',
    rotulo: 'Faltou transferir',
    melhorMaior: false,
  },
  {
    chave: 'taxaPediuOQueTinha',
    rotulo: 'Pediu o que já tinha',
    melhorMaior: false,
  },
  {
    chave: 'taxaResolucao',
    rotulo: 'Resolveu (sim / sim+não)',
    melhorMaior: true,
  },
]

function corDaNota(n: number | null | undefined): string {
  if (n == null) return 'gray'
  if (n >= 8) return 'green'
  if (n >= 6) return 'yellow'
  return 'red'
}

function Delta({
  antes,
  depois,
  melhorMaior,
  nota,
}: {
  antes: number | null
  depois: number | null
  melhorMaior: boolean
  nota?: boolean
}) {
  if (antes == null || depois == null) return <Text as="span">-</Text>
  const d = Math.round((depois - antes) * 10) / 10
  if (d === 0)
    return (
      <Text as="span" color="gray.400">
        0
      </Text>
    )
  const bom = melhorMaior ? d > 0 : d < 0
  return (
    <Text as="span" color={bom ? 'green.300' : 'red.300'} fontWeight="semibold">
      {d > 0 ? '+' : ''}
      {d.toLocaleString('pt-BR')}
      {nota ? '' : ' p.p.'}
    </Text>
  )
}

export default function BotQualityPanel({
  startDate,
  endDate,
}: {
  startDate: string
  endDate: string
}) {
  const navigate = useNavigate()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')

  const [dados, setDados] = useState<Painel | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [agentId, setAgentId] = useState('')
  const [corte, setCorte] = useState('')

  const carregar = useCallback(async () => {
    const DATA_RE = /^\d{4}-\d{2}-\d{2}$/
    if (
      !DATA_RE.test(startDate) ||
      !DATA_RE.test(endDate) ||
      startDate > endDate
    )
      return
    setCarregando(true)
    setErro(null)
    try {
      const res = await api.get('/admin/metrics/bot-quality', {
        params: {
          startDate,
          endDate,
          agentId: agentId || undefined,
          corte: corte || undefined,
        },
      })
      setDados(res.data)
    } catch {
      setErro('Não foi possível carregar a qualidade dos bots.')
    } finally {
      setCarregando(false)
    }
  }, [startDate, endDate, agentId, corte])

  useEffect(() => {
    carregar()
  }, [carregar])

  // Tendência: dias × agentes (nota média)
  const tendencia = useMemo(() => {
    if (!dados?.porDiaAgente?.length) return null
    const dias = [...new Set(dados.porDiaAgente.map((d) => d.dia))].sort()
    const agentes = [
      ...new Map(
        dados.porDiaAgente.map((d) => [d.agentId, d.agente]),
      ).entries(),
    ].sort((a, b) => a[0] - b[0])
    const celula = new Map(
      dados.porDiaAgente.map((d) => [`${d.dia}|${d.agentId}`, d]),
    )
    return { dias, agentes, celula }
  }, [dados])

  const card = {
    bg: surfaceBg,
    borderRadius: '10px',
    borderWidth: '1px',
    borderColor: borderSubtle,
    p: { base: '0.75rem', md: '1rem' },
  }

  if (carregando && !dados) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <Spinner size="lg" color="yellow.500" />
      </Box>
    )
  }

  if (dados?.semDados) {
    return (
      <Box {...card}>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color={textSecondary}>
          {dados.motivo}
        </Text>
      </Box>
    )
  }

  const t = dados?.total
  const comparacao = dados?.comparacao ?? null

  return (
    <VStack align="stretch" spacing={4} w="100%" maxW="100%">
      {/* O que é esta aba */}
      <Box {...card}>
        <Text fontWeight="semibold" fontSize={{ base: 'sm', md: 'md' }} mb={1}>
          Como os bots estão atendendo
        </Text>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color={textSecondary}>
          Todo dia às 04:30 um avaliador (uma IA mais forte,{' '}
          {dados?.config?.model ?? 'o juiz'}) lê uma amostra de até{' '}
          {dados?.config?.perAgent ?? 20} conversas de cada bot do dia anterior
          e confere cada resposta contra a base de conhecimento e os dados reais
          da conta: se estava certa, se inventou alguma informação, se
          transferiu sem precisar e se resolveu o problema do cliente. Nota de 0
          a 10: verde a partir de 8, amarelo de 6 a 7,9, vermelho abaixo de 6.
        </Text>
      </Box>

      {/* Filtros */}
      <Grid
        templateColumns={{ base: '1fr', md: 'auto auto 1fr' }}
        gap={3}
        alignItems="end"
      >
        <Box>
          <Text fontSize="xs" color={textMuted} mb={1}>
            Bot
          </Text>
          <Select
            size="sm"
            w={{ base: '100%', md: '200px' }}
            bg={surfaceBg}
            borderColor={borderSubtle}
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">Todos os bots</option>
            {(dados?.porAgente ?? []).map((a) => (
              <option key={a.agentId} value={a.agentId}>
                {a.agente}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text fontSize="xs" color={textMuted} mb={1}>
            Comparar antes e depois de uma mudança (opcional)
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            <Input
              type="datetime-local"
              size="sm"
              w={{ base: '100%', sm: '220px' }}
              bg={surfaceBg}
              borderColor={borderSubtle}
              value={corte}
              onChange={(e) => setCorte(e.target.value)}
            />
            <Tooltip label="Deploy de referência dos bots (06/10 20:06)">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCorte(CORTE_SUGERIDO)}
              >
                06/10 20:06
              </Button>
            </Tooltip>
            {corte && (
              <Button size="sm" variant="ghost" onClick={() => setCorte('')}>
                Limpar
              </Button>
            )}
          </HStack>
        </Box>
        <Box>{carregando && <Spinner size="sm" color="yellow.500" />}</Box>
      </Grid>

      {erro && (
        <Text color="red.300" fontSize="sm">
          {erro}
        </Text>
      )}

      {dados && !dados.config?.enabled && (
        <Box {...card} borderColor="orange.400">
          <Text fontSize={{ base: 'xs', md: 'sm' }} color={textSecondary}>
            O job diário está desligado
            (support_ai_settings.aiQualityEvalEnabled = 0). Os números abaixo
            vêm só de avaliações sob demanda.
          </Text>
        </Box>
      )}

      {t && t.conversas === 0 ? (
        <Box {...card}>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color={textSecondary}>
            Ainda não há conversas avaliadas neste período
            {corte
              ? ' (a comparação antes × depois também filtra as conversas)'
              : ''}
            . O avaliador roda às 04:30 com as conversas do dia anterior;
            conversas de hoje só aparecem amanhã.
          </Text>
        </Box>
      ) : (
        t && (
          <>
            {/* Cartões */}
            <Grid
              templateColumns={{
                base: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              }}
              gap="1rem"
            >
              {[
                {
                  rotulo: 'Nota média',
                  valor: fmtNota(t.notaMedia),
                  ajuda: `${t.conversas} conversas avaliadas`,
                },
                {
                  rotulo: 'Respostas certas',
                  valor: fmtPct(t.taxaAcerto),
                  ajuda: `das ${t.respostas} respostas do bot que deu para conferir`,
                },
                {
                  rotulo: 'Inventou informação',
                  valor: fmtPct(t.taxaInvencao),
                  ajuda:
                    'das conversas: prazo, valor, regra ou status sem fonte',
                },
                {
                  rotulo: 'Resolveu o problema do cliente',
                  valor: fmtPct(t.taxaResolucao),
                  ajuda: `das conversas (${t.resolucaoIndeterminada} sem como saber ficaram de fora)`,
                },
                {
                  rotulo: 'Passou para humano sem precisar',
                  valor: fmtPct(t.taxaTransfDesnecessaria),
                  ajuda: `e deixou de passar quando devia em ${fmtPct(
                    t.taxaFaltouTransferir,
                  )}`,
                },
                {
                  rotulo: 'Pediu dado que já tinha',
                  valor: fmtPct(t.taxaPediuOQueTinha),
                  ajuda: 'das conversas: pedido, print ou dado já enviado',
                },
                {
                  rotulo: 'Avaliador confere com a equipe',
                  valor: fmtPct(t.taxaConcordancia),
                  ajuda: `dos ${t.corrigidos} chats que um humano corrigiu, o avaliador também viu erro em ${t.juizConcorda}`,
                },
                {
                  rotulo: 'Custo do avaliador no período',
                  valor: `US$ ${(dados?.custoUsd ?? 0).toLocaleString(
                    'pt-BR',
                  )}`,
                  ajuda: `${dados?.config?.model} · ${dados?.config?.effort}`,
                },
              ].map((c) => (
                <GridItem key={c.rotulo} minW={0} {...card}>
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    color={textMuted}
                    noOfLines={1}
                  >
                    {c.rotulo}
                  </Text>
                  <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold">
                    {c.valor}
                  </Text>
                  <Text fontSize="xs" color={textMuted} noOfLines={2}>
                    {c.ajuda}
                  </Text>
                </GridItem>
              ))}
            </Grid>

            {/* Por agente */}
            <Box {...card} p={0} overflowX="auto">
              <Text
                fontWeight="semibold"
                p={{ base: '0.75rem', md: '1rem' }}
                pb={0}
              >
                Boletim por bot
              </Text>
              <Text
                fontSize="xs"
                color={textMuted}
                px={{ base: '0.75rem', md: '1rem' }}
              >
                Percentuais sobre as conversas avaliadas de cada bot. Passe o
                mouse no título da coluna para ver o que ela mede.
              </Text>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Bot</Th>
                    <Th isNumeric>
                      <Tooltip label="Conversas deste bot que o avaliador leu no período">
                        Avaliadas
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Nota média de 0 a 10 dada pelo avaliador">
                        Nota
                      </Tooltip>
                    </Th>
                    <Th>
                      <Tooltip label="Das respostas do bot, quantas estavam corretas">
                        Respostas certas
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Conversas em que o bot afirmou prazo, valor, regra ou status sem fonte (quanto menor, melhor)">
                        Inventou
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Conversas transferidas para humano quando o bot podia ter resolvido (quanto menor, melhor)">
                        Passou sem precisar
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Conversas em que o bot deveria ter transferido e não transferiu (quanto menor, melhor)">
                        Não passou
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Conversas em que o bot pediu um dado que já estava na conversa ou na conta (quanto menor, melhor)">
                        Pediu o que tinha
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Conversas em que o cliente saiu com o problema resolvido">
                        Resolveu
                      </Tooltip>
                    </Th>
                    <Th isNumeric>
                      <Tooltip label="Dos chats avaliados que um humano corrigiu, em quantos o avaliador também viu o erro">
                        Erro confirmado
                      </Tooltip>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dados?.porAgente.map((a) => (
                    <Tr key={a.agentId}>
                      <Td fontWeight="medium">{a.agente}</Td>
                      <Td isNumeric>{a.conversas}</Td>
                      <Td isNumeric>
                        <Badge colorScheme={corDaNota(a.notaMedia)}>
                          {fmtNota(a.notaMedia)}
                        </Badge>
                      </Td>
                      <Td minW="140px">
                        <HStack spacing={2}>
                          <Progress
                            value={a.taxaAcerto ?? 0}
                            size="sm"
                            colorScheme={corDaNota((a.taxaAcerto ?? 0) / 10)}
                            flex={1}
                            borderRadius="full"
                          />
                          <Text fontSize="xs" w="44px" textAlign="right">
                            {fmtPct(a.taxaAcerto)}
                          </Text>
                        </HStack>
                      </Td>
                      <Td isNumeric>{fmtPct(a.taxaInvencao)}</Td>
                      <Td isNumeric>{fmtPct(a.taxaTransfDesnecessaria)}</Td>
                      <Td isNumeric>{fmtPct(a.taxaFaltouTransferir)}</Td>
                      <Td isNumeric>{fmtPct(a.taxaPediuOQueTinha)}</Td>
                      <Td isNumeric>{fmtPct(a.taxaResolucao)}</Td>
                      <Td isNumeric>
                        {a.corrigidos
                          ? `${a.juizConcorda}/${a.corrigidos}`
                          : '-'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Antes × depois */}
            {comparacao && (
              <Box {...card} p={0} overflowX="auto">
                <Text
                  fontWeight="semibold"
                  p={{ base: '0.75rem', md: '1rem' }}
                  pb={0}
                >
                  Antes × depois de {comparacao.corte.slice(8, 10)}/
                  {comparacao.corte.slice(5, 7)}{' '}
                  {comparacao.corte.slice(11, 16)} ({comparacao.antes.conversas}{' '}
                  × {comparacao.depois.conversas} conversas)
                </Text>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Métrica</Th>
                      <Th isNumeric>Antes</Th>
                      <Th isNumeric>Depois</Th>
                      <Th isNumeric>Variação</Th>
                      {comparacao.porAgente.map((a) => (
                        <Th key={a.agentId} isNumeric>
                          {a.agente}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {METRICAS.map((m) => {
                      const c = comparacao
                      const v = (x: Agregado) => x[m.chave] as number | null
                      const fmt = m.nota ? fmtNota : fmtPct
                      return (
                        <Tr key={m.chave}>
                          <Td>{m.rotulo}</Td>
                          <Td isNumeric>{fmt(v(c.antes))}</Td>
                          <Td isNumeric>{fmt(v(c.depois))}</Td>
                          <Td isNumeric>
                            <Delta
                              antes={v(c.antes)}
                              depois={v(c.depois)}
                              melhorMaior={m.melhorMaior}
                              nota={m.nota}
                            />
                          </Td>
                          {c.porAgente.map((a) => (
                            <Td key={a.agentId} isNumeric>
                              <Tooltip
                                label={`${fmt(v(a.antes))} → ${fmt(
                                  v(a.depois),
                                )} (${a.antes.conversas} × ${
                                  a.depois.conversas
                                })`}
                              >
                                <Box as="span">
                                  <Delta
                                    antes={v(a.antes)}
                                    depois={v(a.depois)}
                                    melhorMaior={m.melhorMaior}
                                    nota={m.nota}
                                  />
                                </Box>
                              </Tooltip>
                            </Td>
                          ))}
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}

            {/* Tendência por dia */}
            {tendencia && (
              <Box {...card} p={0} overflowX="auto">
                <Text
                  fontWeight="semibold"
                  p={{ base: '0.75rem', md: '1rem' }}
                  pb={0}
                >
                  Tendência por dia (nota média · acerto)
                </Text>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Dia</Th>
                      <Th isNumeric>Todos</Th>
                      {tendencia.agentes.map(([id, nome]) => (
                        <Th key={id} isNumeric>
                          {nome}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tendencia.dias.map((dia) => {
                      const tot = dados?.porDia.find((d) => d.dia === dia)
                      return (
                        <Tr key={dia}>
                          <Td>{fmtDia(dia)}</Td>
                          <Td isNumeric>
                            <Badge colorScheme={corDaNota(tot?.notaMedia)}>
                              {fmtNota(tot?.notaMedia)}
                            </Badge>{' '}
                            <Text as="span" fontSize="xs" color={textMuted}>
                              {fmtPct(tot?.taxaAcerto)}
                            </Text>
                          </Td>
                          {tendencia.agentes.map(([id]) => {
                            const c = tendencia.celula.get(`${dia}|${id}`)
                            return (
                              <Td key={id} isNumeric>
                                {c ? (
                                  <Tooltip label={`${c.conversas} conversas`}>
                                    <Box as="span">
                                      <Badge
                                        colorScheme={corDaNota(c.notaMedia)}
                                      >
                                        {fmtNota(c.notaMedia)}
                                      </Badge>{' '}
                                      <Text
                                        as="span"
                                        fontSize="xs"
                                        color={textMuted}
                                      >
                                        {fmtPct(c.taxaAcerto)}
                                      </Text>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  '-'
                                )}
                              </Td>
                            )
                          })}
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}

            {/* Piores casos */}
            <CasosLista
              titulo="Piores casos"
              vazio="Nenhum caso com nota abaixo de 7, resposta incorreta ou invenção."
              casos={dados?.piores ?? []}
              card={card}
              textMuted={textMuted}
              abrir={(id) => navigate(`/all-chats/${id}`)}
            />

            {/* Calibração do juiz */}
            {(dados?.discordancias?.length ?? 0) > 0 && (
              <CasosLista
                titulo="Calibração: humano corrigiu, juiz não viu erro"
                vazio=""
                casos={dados?.discordancias ?? []}
                card={card}
                textMuted={textMuted}
                abrir={(id) => navigate(`/all-chats/${id}`)}
              />
            )}
          </>
        )
      )}

      {/* Correções humanas (todos os chats de bot, avaliados ou não) */}
      {(dados?.correcoesHumanas?.length ?? 0) > 0 && (
        <Box {...card} p={0} overflowX="auto">
          <Text
            fontWeight="semibold"
            p={{ base: '0.75rem', md: '1rem' }}
            pb={0}
          >
            Quantas vezes a equipe precisou corrigir o bot
          </Text>
          <Text
            fontSize="xs"
            color={textMuted}
            px={{ base: '0.75rem', md: '1rem' }}
          >
            Conta TODOS os chats do bot no período, avaliados ou não. Um mesmo
            chat pode aparecer em mais de uma coluna.
          </Text>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Bot</Th>
                <Th isNumeric>
                  <Tooltip label="Chats diferentes em que alguém da equipe corrigiu este bot">
                    Chats corrigidos
                  </Tooltip>
                </Th>
                <Th isNumeric>
                  <Tooltip label="Alguém assumiu o chat do bot registrando o erro dele (Correção do bot)">
                    Assumiu para corrigir
                  </Tooltip>
                </Th>
                <Th isNumeric>
                  <Tooltip label='Mensagem ao cliente começando com "Corrigindo o que te passaram"'>
                    Corrigiu o cliente
                  </Tooltip>
                </Th>
                <Th isNumeric>
                  <Tooltip label="Alguém tirou o chat do bot pela tela, sem registrar o motivo">
                    Tirou o chat do bot
                  </Tooltip>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {dados?.correcoesHumanas.map((c) => (
                <Tr key={c.agentId}>
                  <Td>{c.agente}</Td>
                  <Td isNumeric>{c.chats}</Td>
                  <Td isNumeric>{c.correcoes}</Td>
                  <Td isNumeric>{c.correcoesTexto}</Td>
                  <Td isNumeric>{c.intervencoes}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  )
}

function CasosLista({
  titulo,
  vazio,
  casos,
  card,
  textMuted,
  abrir,
}: {
  titulo: string
  vazio: string
  casos: Caso[]
  card: Record<string, unknown>
  textMuted: string
  abrir: (chatId: number) => void
}) {
  return (
    <Box {...card}>
      <Text fontWeight="semibold" mb={3}>
        {titulo}
      </Text>
      {casos.length === 0 ? (
        <Text fontSize="sm" color={textMuted}>
          {vazio}
        </Text>
      ) : (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="1rem">
          {casos.map((c) => (
            <Box
              key={c.chatId}
              borderWidth="1px"
              borderColor="whiteAlpha.200"
              borderRadius="10px"
              p={{ base: '0.75rem', md: '1rem' }}
              minW={0}
            >
              <HStack justify="space-between" flexWrap="wrap" gap={2} mb={2}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme={corDaNota(c.nota)} fontSize="sm">
                    {c.nota}
                  </Badge>
                  <Text fontWeight="medium" fontSize="sm">
                    {c.agente}
                  </Text>
                  <Text fontSize="xs" color={textMuted}>
                    {c.protocolo ?? `#${c.chatId}`} · {fmtDia(c.dia)}
                  </Text>
                  {c.invencao && <Badge colorScheme="red">invenção</Badge>}
                  {c.pediuOQueTinha && (
                    <Badge colorScheme="orange">pediu o que tinha</Badge>
                  )}
                  {c.transferencia === 'desnecessaria' && (
                    <Badge colorScheme="purple">transf. desnecessária</Badge>
                  )}
                  {c.transferencia === 'deveria_ter_transferido' && (
                    <Badge colorScheme="purple">faltou transferir</Badge>
                  )}
                  {c.correcaoHumana && (
                    <Badge colorScheme="blue">corrigido por humano</Badge>
                  )}
                </HStack>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="yellow"
                  onClick={() => abrir(c.chatId)}
                >
                  Abrir chat
                </Button>
              </HStack>
              {c.justificativa && (
                <Text fontSize={{ base: 'xs', md: 'sm' }} mb={2}>
                  {c.justificativa}
                </Text>
              )}
              {c.trechoErrado && (
                <Text
                  fontSize="xs"
                  color="red.300"
                  mb={1}
                  wordBreak="break-word"
                >
                  <b>Bot disse:</b> “{c.trechoErrado}”
                </Text>
              )}
              {c.respostaCerta && (
                <Text
                  fontSize="xs"
                  color="green.300"
                  mb={1}
                  wordBreak="break-word"
                >
                  <b>Certo pela base:</b> {c.respostaCerta}
                </Text>
              )}
              {c.correcaoHumana && (
                <Text
                  fontSize="xs"
                  color="blue.300"
                  wordBreak="break-word"
                  noOfLines={3}
                >
                  <b>Correção humana:</b> {c.correcaoHumana}
                </Text>
              )}
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Box>
  )
}
